import { useState, useEffect, useMemo } from "react";
import { getEmployeeNameById } from "../utils/employeeNames";

/**
 * Reports Dashboard - Time ticket analytics and reporting
 */
export default function ReportsDashboard({ onBackToApp }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard stats
  const [stats, setStats] = useState(null);

  // Report data
  const [reportData, setReportData] = useState([]);
  const [groupBy, setGroupBy] = useState("job");

  // Filters
  const [dateRange, setDateRange] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Selected item for detail view
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Variance analysis
  const [varianceData, setVarianceData] = useState(null);
  const [varianceLoading, setVarianceLoading] = useState(false);


  // Sorting
  const [sortBy, setSortBy] = useState("totalSeconds");
  const [sortDir, setSortDir] = useState("desc");

  // Load dashboard stats
  useEffect(() => {
    loadStats();
  }, []);

  // Load report data when groupBy or filters change
  useEffect(() => {
    loadReportData();
  }, [groupBy, dateRange, customStartDate, customEndDate]);

  const getDateParams = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case "today":
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        break;
      case "week":
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString();
        break;
      case "month":
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        startDate = monthAgo.toISOString();
        break;
      case "custom":
        startDate = customStartDate ? new Date(customStartDate).toISOString() : undefined;
        endDate = customEndDate ? new Date(customEndDate + "T23:59:59").toISOString() : undefined;
        break;
      default:
        // all time
        break;
    }

    return { startDate, endDate };
  };

  const loadStats = async () => {
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const { startDate, endDate } = getDateParams();

      let url = "/api/reports/dashboard-stats";
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (params.toString()) url += "?" + params.toString();

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const { startDate, endDate } = getDateParams();

      let url = `/api/reports/time-summary?groupBy=${groupBy}`;
      if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
      if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to load report");

      const data = await res.json();
      setReportData(data.results || []);
    } catch (err) {
      console.error("Error loading report:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailData = async (type, id) => {
    setDetailLoading(true);
    setVarianceData(null);

    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const { startDate, endDate } = getDateParams();
      let url;

      if (type === "job") {
        url = `/api/reports/job/${id}`;
      } else if (type === "order") {
        url = `/api/reports/order/${id}`;
      } else if (type === "quote") {
        url = `/api/reports/quote/${id}`;
      } else if (type === "employee") {
        url = `/api/reports/employee/${id}`;
        if (startDate) url += `?startDate=${encodeURIComponent(startDate)}`;
        if (endDate) url += `${startDate ? "&" : "?"}endDate=${encodeURIComponent(endDate)}`;
      } else if (type === "operation") {
        url = `/api/reports/operation/${encodeURIComponent(id)}`;
        if (startDate) url += `?startDate=${encodeURIComponent(startDate)}`;
        if (endDate) url += `${startDate ? "&" : "?"}endDate=${encodeURIComponent(endDate)}`;
      }

      if (url) {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          setDetailData(data);
        }
      }
    } catch (err) {
      console.error("Error loading detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Load variance data for a specific job
  const loadVarianceData = async (jobNumber) => {
    setVarianceLoading(true);

    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch(`/api/reports/variance/${jobNumber}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setVarianceData(data);
      }
    } catch (err) {
      console.error("Error loading variance:", err);
    } finally {
      setVarianceLoading(false);
    }
  };


  const handleExport = async () => {
    const token = sessionStorage.getItem("msal.backendToken");
    const { startDate, endDate } = getDateParams();

    let url = "/api/reports/export?format=csv";
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;

    // Add context-aware filters based on current selection
    if (selectedItem) {
      if (selectedItem.type === "job") {
        url += `&jobNumber=${encodeURIComponent(selectedItem.id)}`;
      } else if (selectedItem.type === "quote") {
        url += `&quoteNumber=${encodeURIComponent(selectedItem.id)}`;
      } else if (selectedItem.type === "employee") {
        url += `&employeeId=${encodeURIComponent(selectedItem.id)}`;
      } else if (selectedItem.type === "operation") {
        url += `&operationCode=${encodeURIComponent(selectedItem.id)}`;
      }
    }

    // Open in new tab to download
    window.open(url, "_blank");
  };

  // Toggle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === "desc" ? "asc" : "desc");
    } else {
      setSortBy(column);
      setSortDir("desc");
    }
  };

  // Sort icon helper
  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span className="text-gray-600 ml-1">↕</span>;
    return <span className="text-purple-600 ml-1">{sortDir === "desc" ? "↓" : "↑"}</span>;
  };

  // Filter and sort data
  const filteredData = useMemo(() => {
    let data = reportData;

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter((item) => {
        return (
          item.jobNumber?.toLowerCase().includes(q) ||
          item.jobName?.toLowerCase().includes(q) ||
          item.orderNumber?.toLowerCase().includes(q) ||
          item.quoteNumber?.toLowerCase().includes(q) ||
          item.projectName?.toLowerCase().includes(q) ||
          item.workCenter?.toLowerCase().includes(q) ||
          item.operationCode?.toLowerCase().includes(q) ||
          getEmployeeNameById(item.employeeId)?.toLowerCase().includes(q)
        );
      });
    }

    // Sort data
    data = [...data].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle lastEnd as date
      if (sortBy === "lastEnd") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      // Numeric comparison
      if (typeof aVal === "number" || sortBy === "lastEnd") {
        return sortDir === "desc" ? (bVal || 0) - (aVal || 0) : (aVal || 0) - (bVal || 0);
      }

      // String comparison
      return sortDir === "desc"
        ? String(bVal || "").localeCompare(String(aVal || ""))
        : String(aVal || "").localeCompare(String(bVal || ""));
    });

    return data;
  }, [reportData, searchQuery, sortBy, sortDir]);

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return "0m";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="flex-1 bg-slate-100 text-gray-900 flex flex-col overflow-auto">
      {/* DYNAMIC STATS BAR - shows aggregated stats for current filtered view */}
      {filteredData.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-purple-600">{filteredData.length}</div>
              <div className="text-xs text-gray-500">
                {groupBy === "job" ? "Jobs" :
                 groupBy === "quote" ? "Projects" :
                 groupBy === "workCenter" ? "Work Centers" :
                 groupBy === "employee" ? "Employees" :
                 groupBy === "operation" ? "Operations" :
                 groupBy === "date" ? "Days" : "Items"}
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">
                {filteredData.reduce((sum, item) => sum + (item.ticketCount || 0), 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Tickets</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">
                {formatDuration(filteredData.reduce((sum, item) => sum + (item.totalSeconds || 0), 0))}
              </div>
              <div className="text-xs text-gray-500">Total Time</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-400">
                {filteredData.reduce((sum, item) => sum + (item.totalPieces || 0), 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Pieces</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-pink-400">
                {formatDuration(Math.round(filteredData.reduce((sum, item) => sum + (item.totalSeconds || 0), 0) / filteredData.length))}
              </div>
              <div className="text-xs text-gray-500">Avg Time</div>
            </div>
            </div>
            <button
              onClick={handleExport}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-md text-sm font-medium transition-colors flex items-center gap-2 flex-shrink-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>
      )}

      {/* FILTERS */}
      <div className="bg-slate-100 border-b border-gray-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-4">
          {/* Group By */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">Group by:</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="bg-gray-700 border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="job">Job</option>
              <option value="quote">Quote/Project</option>
              <option value="workCenter">Work Center</option>
              <option value="employee">Employee</option>
              <option value="operation">Operation</option>
              <option value="date">Date</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">Period:</span>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-gray-700 border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom date inputs */}
          {dateRange === "custom" && (
            <>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="bg-gray-700 border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="bg-gray-700 border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </>
          )}

          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search jobs, orders, projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-700 border border-gray-300 rounded px-3 py-1 text-sm"
            />
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex overflow-hidden">
        {/* REPORT TABLE */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-400">{error}</div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">No data found</div>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto">
              <table className="w-full">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    {groupBy === "job" && (
                      <>
                        <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Job</th>
                        <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Order/Quote</th>
                        <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Project</th>
                      </>
                    )}
                    {groupBy === "quote" && (
                      <>
                        <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Quote</th>
                        <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Project</th>
                        <th className="text-right px-4 py-2 text-sm font-medium text-gray-700">Orders</th>
                      </>
                    )}
                    {groupBy === "workCenter" && (
                      <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Work Center</th>
                    )}
                    {groupBy === "employee" && (
                      <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Employee</th>
                    )}
                    {groupBy === "operation" && (
                      <>
                        <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Operation</th>
                        <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Work Center</th>
                      </>
                    )}
                    {groupBy === "date" && (
                      <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Date</th>
                    )}
                    <th
                      className="text-right px-4 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort("ticketCount")}
                    >
                      Tickets<SortIcon column="ticketCount" />
                    </th>
                    <th
                      className="text-right px-4 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort("totalSeconds")}
                    >
                      Time<SortIcon column="totalSeconds" />
                    </th>
                    <th
                      className="text-right px-4 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort("totalPieces")}
                    >
                      Pieces<SortIcon column="totalPieces" />
                    </th>
                    <th
                      className="text-right px-4 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort("lastEnd")}
                    >
                      Last Activity<SortIcon column="lastEnd" />
                    </th>
                    {groupBy !== "job" && groupBy !== "quote" && (
                      <th
                        className="text-right px-4 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900"
                        onClick={() => handleSort("jobCount")}
                      >
                        Jobs<SortIcon column="jobCount" />
                      </th>
                    )}
                    {groupBy === "quote" && (
                      <th
                        className="text-right px-4 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900"
                        onClick={() => handleSort("jobCount")}
                      >
                        Jobs<SortIcon column="jobCount" />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, idx) => (
                    <tr
                      key={idx}
                      onClick={() => {
                        if (groupBy === "job" && item.jobNumber) {
                          setSelectedItem({ type: "job", id: item.jobNumber });
                          loadDetailData("job", item.jobNumber);
                        } else if (groupBy === "quote" && item.quoteNumber) {
                          setSelectedItem({ type: "quote", id: item.quoteNumber });
                          loadDetailData("quote", item.quoteNumber);
                        } else if (groupBy === "employee" && item.employeeId) {
                          setSelectedItem({ type: "employee", id: item.employeeId, name: getEmployeeNameById(item.employeeId) });
                          loadDetailData("employee", item.employeeId);
                        } else if (groupBy === "operation" && item.operationCode) {
                          setSelectedItem({ type: "operation", id: item.operationCode, workCenter: item.workCenter });
                          loadDetailData("operation", item.operationCode);
                        }
                      }}
                      className={`border-b border-gray-200 hover:bg-slate-100 ${["job", "quote", "employee", "operation"].includes(groupBy) ? "cursor-pointer" : ""}`}
                    >
                      {groupBy === "job" && (
                        <>
                          <td className="px-4 py-3">
                            <div className="font-medium">{item.jobNumber}</div>
                            <div className="text-xs text-gray-500">{item.jobName}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {item.orderNumber}
                            {item.quoteNumber && <span className="text-gray-500 ml-1">({item.quoteNumber})</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">
                            {item.projectName}
                          </td>
                        </>
                      )}
                      {groupBy === "quote" && (
                        <>
                          <td className="px-4 py-3 font-medium">{item.quoteNumber || "No Quote"}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 max-w-[250px] truncate">
                            {item.projectName || "—"}
                          </td>
                          <td className="px-4 py-3 text-right text-sm">{item.orderCount || 0}</td>
                        </>
                      )}
                      {groupBy === "workCenter" && (
                        <td className="px-4 py-3 font-medium">{item.workCenter || "Unknown"}</td>
                      )}
                      {groupBy === "employee" && (
                        <td className="px-4 py-3 font-medium">
                          {getEmployeeNameById(item.employeeId) || `Employee ${item.employeeId}`}
                        </td>
                      )}
                      {groupBy === "operation" && (
                        <>
                          <td className="px-4 py-3 font-medium">{item.operationCode}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{item.workCenter}</td>
                        </>
                      )}
                      {groupBy === "date" && (
                        <td className="px-4 py-3 font-medium">{item.date}</td>
                      )}
                      <td className="px-4 py-3 text-right text-sm">{item.ticketCount}</td>
                      <td className="px-4 py-3 text-right font-medium text-green-400">
                        {formatDuration(item.totalSeconds)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm">{item.totalPieces?.toLocaleString() || 0}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-500">
                        {formatDate(item.lastEnd)}
                      </td>
                      {groupBy !== "job" && groupBy !== "quote" && (
                        <td className="px-4 py-3 text-right text-sm text-gray-500">{item.jobCount}</td>
                      )}
                      {groupBy === "quote" && (
                        <td className="px-4 py-3 text-right text-sm text-gray-500">{item.jobCount}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* DETAIL PANEL */}
        {selectedItem && (
          <div className="w-[450px] bg-white border-l border-gray-200 p-4 overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">
                {selectedItem.type === "quote" ? "Project Details" :
                 selectedItem.type === "employee" ? "Employee Details" :
                 selectedItem.type === "operation" ? "Operation Details" :
                 "Job Details"}
              </h3>
              <button
                onClick={() => {
                  setSelectedItem(null);
                  setDetailData(null);
                  setVarianceData(null);
                }}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {detailLoading ? (
              <div className="text-gray-500">Loading...</div>
            ) : selectedItem.type === "quote" && detailData?.summary ? (
              /* QUOTE/PROJECT DETAIL VIEW */
              <div className="space-y-4">
                {/* Summary */}
                <div>
                  <div className="text-lg font-bold">{detailData.summary.quoteNumber}</div>
                  <div className="text-sm text-gray-500">{detailData.summary.projectName}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {detailData.summary.orderCount} orders • {detailData.summary.jobCount} jobs
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-green-400">
                      {formatDuration(detailData.summary.totalSeconds)}
                    </div>
                    <div className="text-xs text-gray-500">Total Time</div>
                  </div>
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-blue-400">
                      {detailData.summary.ticketCount}
                    </div>
                    <div className="text-xs text-gray-500">Tickets</div>
                  </div>
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-purple-600">
                      {detailData.summary.totalPieces?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-gray-500">Pieces</div>
                  </div>
                </div>

                {/* By Work Center */}
                {detailData.byWorkCenter?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Time by Work Center</h4>
                    <div className="space-y-2">
                      {detailData.byWorkCenter.map((wc, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-slate-100 rounded px-3 py-2">
                          <div>
                            <div className="text-sm font-medium">{wc.workCenter}</div>
                            <div className="text-xs text-gray-500">{wc.jobCount} jobs</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-green-400">{formatDuration(wc.totalSeconds)}</div>
                            <div className="text-xs text-gray-500">{wc.ticketCount} tickets</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Operation */}
                {detailData.byOperation?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Time by Operation</h4>
                    <div className="space-y-2 max-h-48 overflow-auto">
                      {detailData.byOperation.map((op, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-slate-100 rounded px-3 py-2">
                          <div>
                            <div className="text-sm font-medium">{op.operationCode}</div>
                            <div className="text-xs text-gray-500">{op.workCenter}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-green-400">{formatDuration(op.totalSeconds)}</div>
                            <div className="text-xs text-gray-500">avg {formatDuration(op.avgSeconds)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Jobs List */}
                {detailData.byJob?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Jobs ({detailData.byJob.length})</h4>
                    <div className="space-y-1 max-h-48 overflow-auto">
                      {detailData.byJob.map((job) => (
                        <div
                          key={job.jobNumber}
                          className="text-xs bg-slate-100 rounded px-2 py-2 cursor-pointer hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem({ type: "job", id: job.jobNumber });
                            loadDetailData("job", job.jobNumber);
                          }}
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">{job.jobNumber}</span>
                            <span className="text-green-400">{formatDuration(job.totalSeconds)}</span>
                          </div>
                          <div className="text-gray-500 truncate">{job.jobName}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : selectedItem.type === "job" && detailData?.summary ? (
              /* JOB DETAIL VIEW */
              <div className="space-y-4">
                {/* Summary */}
                <div>
                  <div className="text-lg font-bold">{detailData.summary.jobNumber}</div>
                  <div className="text-sm text-gray-500">{detailData.summary.jobName}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {detailData.summary.orderNumber} • {detailData.summary.quoteNumber}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-green-400">
                      {formatDuration(detailData.summary.totalSeconds)}
                    </div>
                    <div className="text-xs text-gray-500">Total Time</div>
                  </div>
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-blue-400">
                      {detailData.summary.totalPieces}
                    </div>
                    <div className="text-xs text-gray-500">Pieces</div>
                  </div>
                </div>

                {/* Variance Analysis Button */}
                <button
                  onClick={() => loadVarianceData(detailData.summary.jobNumber)}
                  disabled={varianceLoading}
                  className="w-full px-3 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 rounded text-sm font-medium transition-colors"
                >
                  {varianceLoading ? "Loading..." : "View Estimated vs Actual"}
                </button>

                {/* Variance Analysis Results */}
                {varianceData && (
                  <div className="bg-slate-100 rounded p-3">
                    <h4 className="text-sm font-medium text-amber-400 mb-3">Variance Analysis</h4>

                    {/* Summary */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-700">
                          {varianceData.summary.totalEstimatedHours.toFixed(1)}h
                        </div>
                        <div className="text-xs text-gray-500">Estimated</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-400">
                          {varianceData.summary.totalActualHours.toFixed(1)}h
                        </div>
                        <div className="text-xs text-gray-500">Actual</div>
                      </div>
                    </div>

                    {/* Variance indicator */}
                    <div className={`text-center p-2 rounded mb-3 ${
                      varianceData.summary.totalVarianceHours > 0
                        ? "bg-red-100 border border-red-700"
                        : varianceData.summary.totalVarianceHours < 0
                        ? "bg-green-100 border border-green-700"
                        : "bg-gray-700"
                    }`}>
                      <div className={`text-lg font-bold ${
                        varianceData.summary.totalVarianceHours > 0 ? "text-red-400" : "text-green-400"
                      }`}>
                        {varianceData.summary.totalVarianceHours > 0 ? "+" : ""}
                        {varianceData.summary.totalVarianceHours.toFixed(2)}h
                        ({varianceData.summary.totalVariancePercent > 0 ? "+" : ""}
                        {varianceData.summary.totalVariancePercent.toFixed(0)}%)
                      </div>
                      <div className="text-xs text-gray-500">
                        {varianceData.summary.totalVarianceHours > 0 ? "Over estimate" : "Under estimate"}
                      </div>
                    </div>

                    {/* By Operation */}
                    <h5 className="text-xs font-medium text-gray-500 mb-2">By Operation</h5>
                    <div className="space-y-1 max-h-40 overflow-auto">
                      {varianceData.operations.filter(op => op.hasActualTime || op.hasEstimate).map((op, idx) => (
                        <div key={idx} className="text-xs bg-white rounded px-2 py-1">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{op.operationCode}</span>
                            <span className={`font-medium ${
                              op.varianceHours > 0 ? "text-red-400" : "text-green-400"
                            }`}>
                              {op.varianceHours > 0 ? "+" : ""}{op.varianceHours.toFixed(2)}h
                            </span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span>Est: {op.estimatedHours.toFixed(2)}h</span>
                            <span>Act: {op.actualHours.toFixed(2)}h</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Work Center */}
                {detailData.byWorkCenter?.length > 0 && !varianceData && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Work Center</h4>
                    <div className="space-y-2">
                      {detailData.byWorkCenter.map((wc, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-slate-100 rounded px-3 py-2">
                          <div>
                            <div className="text-sm font-medium">{wc.workCenter}</div>
                            <div className="text-xs text-gray-500">{wc.operationCode}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-green-400">{formatDuration(wc.totalSeconds)}</div>
                            <div className="text-xs text-gray-500">{wc.ticketCount} tickets</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Tickets */}
                {detailData.tickets?.length > 0 && !varianceData && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Tickets</h4>
                    <div className="space-y-1 max-h-48 overflow-auto">
                      {detailData.tickets.slice(0, 10).map((ticket) => (
                        <div key={ticket.id} className="text-xs bg-slate-100 rounded px-2 py-1">
                          <div className="flex justify-between">
                            <span>{ticket.operationCode}</span>
                            <span className="text-green-400">{formatDuration(ticket.durationSeconds)}</span>
                          </div>
                          <div className="text-gray-500">{formatDate(ticket.startTime)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : selectedItem.type === "employee" && detailData?.summary ? (
              /* EMPLOYEE DETAIL VIEW */
              <div className="space-y-4">
                {/* Summary */}
                <div>
                  <div className="text-lg font-bold">{selectedItem.name || `Employee ${detailData.summary.employeeId}`}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {detailData.summary.quoteCount} projects • {detailData.summary.jobCount} jobs • {detailData.summary.operationCount} operations
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-green-400">
                      {formatDuration(detailData.summary.totalSeconds)}
                    </div>
                    <div className="text-xs text-gray-500">Total Time</div>
                  </div>
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-blue-400">
                      {detailData.summary.ticketCount}
                    </div>
                    <div className="text-xs text-gray-500">Tickets</div>
                  </div>
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-purple-600">
                      {detailData.summary.totalPieces?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-gray-500">Pieces</div>
                  </div>
                </div>

                {/* By Quote/Project */}
                {detailData.byQuote?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Project ({detailData.byQuote.length})</h4>
                    <div className="space-y-1 max-h-40 overflow-auto">
                      {detailData.byQuote.map((q, idx) => (
                        <div
                          key={idx}
                          className="text-xs bg-slate-100 rounded px-2 py-2 cursor-pointer hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (q.quoteNumber) {
                              setSelectedItem({ type: "quote", id: q.quoteNumber });
                              loadDetailData("quote", q.quoteNumber);
                            }
                          }}
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">{q.quoteNumber || "No Quote"}</span>
                            <span className="text-green-400">{formatDuration(q.totalSeconds)}</span>
                          </div>
                          <div className="text-gray-500 truncate">{q.projectName}</div>
                          <div className="text-gray-600 mt-0.5">{q.jobCount} jobs • {q.ticketCount} tickets</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Job */}
                {detailData.byJob?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Job ({detailData.byJob.length})</h4>
                    <div className="space-y-1 max-h-40 overflow-auto">
                      {detailData.byJob.slice(0, 15).map((job) => (
                        <div
                          key={job.jobNumber}
                          className="text-xs bg-slate-100 rounded px-2 py-2 cursor-pointer hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem({ type: "job", id: job.jobNumber });
                            loadDetailData("job", job.jobNumber);
                          }}
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">{job.jobNumber}</span>
                            <span className="text-green-400">{formatDuration(job.totalSeconds)}</span>
                          </div>
                          <div className="text-gray-500 truncate">{job.jobName}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Operation */}
                {detailData.byOperation?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Operation</h4>
                    <div className="space-y-1 max-h-36 overflow-auto">
                      {detailData.byOperation.map((op, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-slate-100 rounded px-2 py-1.5 text-xs">
                          <div>
                            <span className="font-medium">{op.operationCode}</span>
                            <span className="text-gray-500 ml-2">{op.workCenter}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-green-400">{formatDuration(op.totalSeconds)}</span>
                            <span className="text-gray-500 ml-2">{op.jobCount} jobs</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Work Center */}
                {detailData.byWorkCenter?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Work Center</h4>
                    <div className="space-y-1">
                      {detailData.byWorkCenter.map((wc, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-slate-100 rounded px-2 py-1.5 text-xs">
                          <span className="font-medium">{wc.workCenter}</span>
                          <div>
                            <span className="text-green-400">{formatDuration(wc.totalSeconds)}</span>
                            <span className="text-gray-500 ml-2">{wc.ticketCount} tickets</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : selectedItem.type === "operation" && detailData?.summary ? (
              /* OPERATION DETAIL VIEW */
              <div className="space-y-4">
                {/* Summary */}
                <div>
                  <div className="text-lg font-bold">{detailData.summary.operationCode}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {detailData.summary.quoteCount} projects • {detailData.summary.jobCount} jobs • {detailData.summary.employeeCount} employees
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-green-400">
                      {formatDuration(detailData.summary.totalSeconds)}
                    </div>
                    <div className="text-xs text-gray-500">Total Time</div>
                  </div>
                  <div className="bg-slate-100 rounded p-3 text-center">
                    <div className="text-xl font-bold text-blue-400">
                      {detailData.summary.ticketCount}
                    </div>
                    <div className="text-xs text-gray-500">Tickets</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-100 rounded p-2 text-center">
                    <div className="text-sm font-bold text-yellow-400">
                      {formatDuration(detailData.summary.avgSeconds)}
                    </div>
                    <div className="text-xs text-gray-500">Avg</div>
                  </div>
                  <div className="bg-slate-100 rounded p-2 text-center">
                    <div className="text-sm font-bold text-cyan-600">
                      {formatDuration(detailData.summary.minSeconds)}
                    </div>
                    <div className="text-xs text-gray-500">Min</div>
                  </div>
                  <div className="bg-slate-100 rounded p-2 text-center">
                    <div className="text-sm font-bold text-red-400">
                      {formatDuration(detailData.summary.maxSeconds)}
                    </div>
                    <div className="text-xs text-gray-500">Max</div>
                  </div>
                </div>

                {/* By Quote/Project */}
                {detailData.byQuote?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Project ({detailData.byQuote.length})</h4>
                    <div className="space-y-1 max-h-36 overflow-auto">
                      {detailData.byQuote.map((q, idx) => (
                        <div
                          key={idx}
                          className="text-xs bg-slate-100 rounded px-2 py-2 cursor-pointer hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (q.quoteNumber) {
                              setSelectedItem({ type: "quote", id: q.quoteNumber });
                              loadDetailData("quote", q.quoteNumber);
                            }
                          }}
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">{q.quoteNumber || "No Quote"}</span>
                            <span className="text-green-400">{formatDuration(q.totalSeconds)}</span>
                          </div>
                          <div className="text-gray-500 truncate">{q.projectName}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Job */}
                {detailData.byJob?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Job ({detailData.byJob.length})</h4>
                    <div className="space-y-1 max-h-36 overflow-auto">
                      {detailData.byJob.slice(0, 15).map((job) => (
                        <div
                          key={job.jobNumber}
                          className="text-xs bg-slate-100 rounded px-2 py-2 cursor-pointer hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem({ type: "job", id: job.jobNumber });
                            loadDetailData("job", job.jobNumber);
                          }}
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">{job.jobNumber}</span>
                            <span className="text-green-400">{formatDuration(job.totalSeconds)}</span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span className="truncate max-w-[200px]">{job.jobName}</span>
                            <span>avg {formatDuration(job.avgSeconds)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Employee */}
                {detailData.byEmployee?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Employee ({detailData.byEmployee.length})</h4>
                    <div className="space-y-1 max-h-36 overflow-auto">
                      {detailData.byEmployee.map((emp, idx) => (
                        <div
                          key={idx}
                          className="text-xs bg-slate-100 rounded px-2 py-2 cursor-pointer hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem({ type: "employee", id: emp.employeeId, name: getEmployeeNameById(emp.employeeId) });
                            loadDetailData("employee", emp.employeeId);
                          }}
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">{getEmployeeNameById(emp.employeeId)}</span>
                            <span className="text-green-400">{formatDuration(emp.totalSeconds)}</span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span>{emp.ticketCount} tickets • {emp.jobCount} jobs</span>
                            <span>avg {formatDuration(emp.avgSeconds)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Work Center */}
                {detailData.byWorkCenter?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Work Center</h4>
                    <div className="space-y-1">
                      {detailData.byWorkCenter.map((wc, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-slate-100 rounded px-2 py-1.5 text-xs">
                          <span className="font-medium">{wc.workCenter}</span>
                          <div>
                            <span className="text-green-400">{formatDuration(wc.totalSeconds)}</span>
                            <span className="text-gray-500 ml-2">{wc.employeeCount} employees</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500">Select an item to see details</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
